import React, { useEffect, useState } from "react";

import {
    RiCloseLine,
    RiExternalLinkLine,
    RiEyeLine,
    RiLayout5Line,
} from "@remixicon/react";
import classNames from "classnames";
import {
    DashboardGroupBy,
    DashboardInput,
    DashboardSettingsFiltersInput,
    DashboardSortBy,
    DashboardTicketDisplay, DashboardView,
} from "../../../../__generated__/api";
import Dropdown from "../../../theme/dropdown/Dropdown";
import Styles from "../BoardToolbar.module.scss";
import { FiltersView } from "../Filters";

import { Link, Paths } from "../../../../contexts/Routing";
import ExpandingSection from "../../../expanding-section/ExpandingSection";
import Button from "../../../theme/button/Button";
import FormGroup from "../../../theme/form-group/FormGroup";
import GroupByFilter from "../GroupBy";
import SortByFilter from "../SortBy";
import CardLayout from "./CardLayout";
import ColumnSettings from "./ColumnSettings";
import LayoutPicker, { BOARD_LAYOUT_OPTIONS, TICKET_DETAILS_OPTIONS } from "./LayoutPicker";
import ShareOptionsSettings from "./ShareOptions";

export function ViewOptions({
    onChange,
    value,
    shouldShowCreate,
    className,

}: {
    onChange: (value?: DashboardInput) => void;
    value: DashboardInput | undefined;
    shouldShowCreate: boolean;
    className?: string
}) {
    return (
        <div className={className}>
            <ExpandingSection title="View Options" defaultExpanded>
                <div>
                    <div className="text-body-2-medium m3b-1">Board Layout</div>
                    <LayoutPicker
                        options={BOARD_LAYOUT_OPTIONS}
                        value={value?.settings?.view as DashboardView}
                        onChange={view => onChange({
                            ...value,
                            settings: {
                                ...value?.settings,
                                view,
                            },
                        })}
                        defaultValue={DashboardView.Kanban}
                    />
                </div>
                <div className="m3t-2">
                    <div className="text-body-2-medium m3b-1 m3t-1">Ticket Details</div>
                    <LayoutPicker
                        options={TICKET_DETAILS_OPTIONS}
                        value={value?.settings?.ticketDisplay as DashboardTicketDisplay}
                        defaultValue={DashboardTicketDisplay.SideRight}
                        onChange={ticketDisplay => onChange({
                            ...value,
                            settings: {
                                ...value?.settings,
                                ticketDisplay,
                            },
                        })}
                    />
                </div>
            </ExpandingSection>

            {shouldShowCreate && (
                <ExpandingSection title="Name" defaultExpanded cardClassName="m3t-1 border-top rounded-0">
                    <FormGroup
                        type="text"
                        size="filter"
                        className="text-body-1-medium"
                        value={value?.name ?? "My Board"}
                        prefixIcon={<RiLayout5Line />}
                        onChange={event => onChange({
                            ...value,
                            name: event.target.value,
                        })}
                    />
                </ExpandingSection>
            )}

            {shouldShowCreate && (

                <ExpandingSection title="Filters" defaultExpanded cardClassName="m3t-1 border-top rounded-0">
                    <FiltersView
                        shouldPortal
                        value={value?.settings?.filters as DashboardSettingsFiltersInput}
                        onChange={filters => onChange({
                            ...value,
                            settings: {
                                ...value?.settings,
                                filters,
                            },
                        })}
                    />
                </ExpandingSection>
            )}
            <ExpandingSection
                title={(
                    <div>
                        Column Settings
                        <Link
                            to={Paths.ADMINISTRATOR_SETTING}
                            target="_blank"
                            params={{ setting: "ticket-states" }}
                            className="m3s-1 align-text-bottom"
                            onClick={evt => evt.stopPropagation()}
                        >
                            <RiExternalLinkLine className="icon-secondary icon-small" />
                        </Link>
                    </div>
                )}
                defaultExpanded
                cardClassName="m3t-1 border-top rounded-0"
            >
                <ColumnSettings
                    value={value?.ticketStates ?? []}
                    onChange={ticketStates => {
                        onChange({
                            ...value,
                            ticketStates,
                        });
                    }}
                />
            </ExpandingSection>

            {shouldShowCreate && (
                <ExpandingSection title="Sorting" defaultExpanded cardClassName="m3t-1 border-top rounded-0">
                    <SortByFilter
                        value={value?.settings?.sortBy as DashboardSortBy}
                        shouldShowExpand
                        onChange={sortBy => onChange({
                            ...value,
                            settings: {
                                ...value?.settings,
                                sortBy,
                            },
                        })}
                    />
                </ExpandingSection>
            )}
            {shouldShowCreate && (
                <ExpandingSection title="Grouping" defaultExpanded cardClassName="m3t-1 border-top rounded-0">
                    <GroupByFilter
                        value={value?.settings?.groupBy as DashboardGroupBy}
                        shouldShowExpand
                        onChange={groupBy => onChange({
                            ...value,
                            settings: {
                                ...value?.settings,
                                groupBy,
                            },
                        })}
                    />
                </ExpandingSection>
            )}

            <ExpandingSection title="Card Layout" defaultExpanded cardClassName="m3t-1 border-top rounded-0">
                <CardLayout
                    value={value?.settings?.fieldIds ?? []}
                    onChange={fieldIds => {
                        onChange({
                            ...value,
                            settings: {
                                ...value?.settings,
                                fieldIds,
                            },
                        });
                    }}
                />
            </ExpandingSection>
            {shouldShowCreate && (
                <ExpandingSection title="Share Options" defaultExpanded cardClassName="m3t-1 border-top rounded-0">
                    <ShareOptionsSettings
                        shareTypeLabel="Dashboard"
                        value={value?.permissions ?? []}
                        onChange={permissions => {
                            onChange({
                                ...value,
                                permissions,
                            });
                        }}
                    />
                </ExpandingSection>
            )}

        </div>
    );
}
ViewOptions.defaultProps = {
    className: "",
};

export function ViewOptionsFilter({
    onChange,
    value,
}: {
    onChange: (value?: DashboardInput) => void;
    value: DashboardInput | undefined
}) {
    const [shouldShow, setShouldShow] = useState(false);

    return (
        <Dropdown autoClose="outside" show={shouldShow} onToggle={() => setShouldShow(!shouldShow)}>
            <Dropdown.Toggle
                variant="filter"
                prefixIcon={<RiEyeLine />}
                onClick={() => setShouldShow(!shouldShow)}
                isActive={shouldShow}
            >
                Show
            </Dropdown.Toggle>
            <Dropdown.Menu className={`p3-0 ${Styles["panel-view-options"]}`}>
                <ViewOptions onChange={onChange} value={value} shouldShowCreate={false} />
            </Dropdown.Menu>
        </Dropdown>
    );
}

export function CreateEditView({
    onChange,
    shouldShow,
    isEditing,
    onHide,
    value,
}: {
    onChange: (value?: DashboardInput) => void;
    onHide: () => void;
    isEditing?: boolean;
    shouldShow: boolean;
    value?: DashboardInput;
}) {
    const [current, setCurrent] = useState(value);
    useEffect(() => {
        setCurrent(value);
    }, [value]);

    useEffect(() => {
        if (shouldShow && !value) {
            setCurrent({
                settings: {
                    ticketDisplay: DashboardTicketDisplay.SideRight,
                    view: DashboardView.Kanban,
                },
            });
        }
    }, [shouldShow]);

    return (
        <div className={classNames("offcanvas offcanvas-end", shouldShow ? "show" : "")} tabIndex={-1}>
            <div className="offcanvas-header d-flex justify-content-between border-bottom">
                <h4>{isEditing ? "Edit View" : "Add View"}</h4>
                <Button variant="none" size="small" onClick={onHide} prefixIcon={<RiCloseLine />} />
            </div>
            <div className="offcanvas-body p-0 p3e-105">
                <ViewOptions onChange={setCurrent} value={current} shouldShowCreate className="border-end" />
                <div className="border-top w-100 d-flex justify-content-end gap-2 p3y-1">
                    <Button variant="outline-gray" onClick={onHide}>Cancel</Button>
                    <Button onClick={() => onChange(current)}>Save</Button>
                </div>
            </div>
        </div>
    );
}

CreateEditView.defaultProps = {
    value: undefined,
    isEditing: false,
};
